/**
 * The ONE fallback every cp label map falls through to when it is asked for a constant it does not
 * know — and the reason none of them may fall through to an empty string.
 *
 * ── THE FAILURE THIS PREVENTS
 *
 * Every cp enum is a POSITION-BASED mirror of a backend `AlphaBaseEnum`, and every one of those
 * mirrors carries the same instruction in its TSDoc: new constants are APPENDED at the end. Appending
 * is therefore an expected, routine backend change — and it reaches the frontend as a number the
 * TypeScript enum has never heard of.
 *
 * A label map answering that number with `''` renders the cell EMPTY. Nothing fails: no compile
 * error, because the new ordinal is just a number; no runtime error, because `''` is a perfectly
 * good string; no warning anywhere. The booking is listed, its "Ereignis" column is simply blank,
 * and the operator reads a blank cell as "this booking has no provenance" rather than as "this
 * screen is one release behind the backend". On the screen payouts are decided on, a silently
 * blank cell is the worst of the three possible outcomes.
 *
 * The fallback below is the loud one: it prints the ENUM KEY when TypeScript still knows the
 * constant but no German label was written for it, and `#<ordinal>` when it does not know the
 * constant at all. Both are ugly on purpose — ugly is visible, and visible gets fixed.
 *
 * A genuinely ABSENT value (`null` / `undefined`) is a different statement and stays empty: the row
 * has no such attribute, which is not a mapping gap.
 */
export function provisionEnumLabelFallback(enumObject: object, value: number): string {

    if (value == null) {
        return '';
    }

    // Reverse lookup of a numeric TypeScript enum: known ordinal -> its key, unknown ordinal ->
    // undefined. The `typeof` guard is what separates the two, and it also rejects the numeric
    // entries of the double mapping should a string ever be passed in.
    const key: unknown = (enumObject as Record<number, unknown>)[value];

    return typeof key === 'string' ? key : '#' + value;
}
